import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { GradeRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Grades & sections. Optional today (the student form falls
 * back to free text when the structure is empty). Once you add grades and
 * sections here, the student form uses dropdowns.
 */
export function StructurePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addGradeOpen, setAddGradeOpen] = useState(false);
  const [addSectionUnder, setAddSectionUnder] = useState<GradeRow | null>(null);

  const q = useQuery({
    queryKey: ['structure', institution],
    queryFn: () => productApi.getStructure(institution),
    enabled: !!institution,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['structure', institution] });

  const delGrade = useMutation({
    mutationFn: (id: string) => productApi.deleteGrade(institution, id),
    onSuccess: () => { invalidate(); void message.success('Grade removed'); },
    onError: (err) => { void message.error(errMsg(err) ?? 'Delete failed'); },
  });
  const delSection = useMutation({
    mutationFn: (id: string) => productApi.deleteSection(institution, id),
    onSuccess: () => { invalidate(); void message.success('Section removed'); },
    onError: (err) => { void message.error(errMsg(err) ?? 'Delete failed'); },
  });

  const grades = q.data?.grades ?? [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Grades &amp; sections</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            The atomic naming every module reads. Optional — the student form falls back to free text
            until you define these.
          </Typography.Text>
        </div>
        <Space size={8}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddGradeOpen(true)}>
            Add grade
          </Button>
        </Space>
      </div>

      {q.isError && (
        <Alert type="error" showIcon message="Could not load structure" description={String(q.error)} />
      )}

      {grades.length === 0 ? (
        <Card>
          <Empty
            description={
              <span>
                No grades defined yet. <a onClick={() => setAddGradeOpen(true)}>Add the first grade</a>.
              </span>
            }
            style={{ padding: 24 }}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
          {grades.map((g) => (
            <Card
              key={g.id}
              size="small"
              title={
                <Space size={8}>
                  <span style={{ fontWeight: 600 }}>{g.name}</span>
                  <Tag>rank {g.rank}</Tag>
                  <Tag>{g.sections.length} section{g.sections.length === 1 ? '' : 's'}</Tag>
                </Space>
              }
              extra={
                <Popconfirm
                  title="Delete this grade?"
                  description="Only if it has no sections and no students."
                  onConfirm={() => delGrade.mutate(g.id)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                </Popconfirm>
              }
            >
              {g.sections.length === 0 ? (
                <Empty style={{ padding: 8 }} description="No sections" />
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  dataSource={g.sections}
                  showHeader={false}
                  columns={[
                    { title: 'Section', dataIndex: 'name', key: 'name' },
                    {
                      title: '',
                      key: 'action',
                      width: 40,
                      align: 'right',
                      render: (_, s) => (
                        <Popconfirm
                          title="Delete this section?"
                          onConfirm={() => delSection.mutate(s.id)}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                        >
                          <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              )}
              <div style={{ marginTop: 10 }}>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setAddSectionUnder(g)}
                >
                  Add section
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddGradeModal
        open={addGradeOpen}
        onClose={() => setAddGradeOpen(false)}
        institution={institution}
        onCreated={() => { invalidate(); setAddGradeOpen(false); void message.success('Grade added'); }}
      />
      <AddSectionModal
        grade={addSectionUnder}
        onClose={() => setAddSectionUnder(null)}
        institution={institution}
        onCreated={() => { invalidate(); setAddSectionUnder(null); void message.success('Section added'); }}
      />
    </Space>
  );
}

function AddGradeModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ name: string; rank: number }>();
  const m = useMutation({
    mutationFn: (v: { name: string; rank: number }) => productApi.createGrade(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add grade" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ rank: 0 }} onFinish={(v) => m.mutate({ name: v.name.trim(), rank: v.rank })}>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="9 or FYBSc" autoFocus /></Form.Item>
        <Form.Item name="rank" label="Sort order" tooltip="Lower shows first in dropdowns.">
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function AddSectionModal({
  grade, onClose, institution, onCreated,
}: { grade: GradeRow | null; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ name: string }>();
  const m = useMutation({
    mutationFn: (v: { name: string }) =>
      productApi.createSection(institution, { grade_id: grade!.id, name: v.name.trim() }),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={!!grade} title={`Add section under ${grade?.name ?? ''}`} onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)}>
        <Form.Item name="name" label="Section name" rules={[{ required: true }]}><Input placeholder="A" autoFocus /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
